import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
  Divider,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import usePermissions from '../../utils/permissions';
import { CARD_PERMISSION_MAP } from '../../api/aiCardsApi';

const AiCardBase = ({
  cardType,
  label,
  children,
  onConfirm,
  isConfirmed = false,
  isConfirming = false,
  isUpdatedByAi = true,
  category = 'CLINICAL',
  customAction = null,
  icon = null,
  isEditedByDoctor = false,
  draftSavedAt = null,
}) => {
  const { hasPermission } = usePermissions();
  const requiredPermission = CARD_PERMISSION_MAP[cardType];
  const canConfirm = !requiredPermission || hasPermission(requiredPermission);

  return (
    <Card
      elevation={isConfirmed ? 1 : 3}
      sx={{
        borderRadius: 3,
        bgcolor: isConfirmed ? '#F8FAFC' : '#FFFFFF',
        transition: 'all 0.25s ease-in-out',
        position: 'relative',
        overflow: 'hidden',
      }}
      data-testid={`ai-card-${cardType}`}
    >

      <CardHeader
        avatar={
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isConfirmed ? '#E6F4EA' : '#F3E8FF',
              color: isConfirmed ? '#0F9D58' : '#7C3AED',
            }}
          >
            {isConfirmed ? (
              <CheckCircleIcon sx={{ fontSize: 22 }} />
            ) : icon ? (
              icon
            ) : (
              <AutoAwesomeIcon sx={{ fontSize: 20 }} />
            )}
          </Box>
        }
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16 }}>
              {label || cardType}
            </Typography>
            {isConfirmed && (
              <Chip
                label="Confirmed & Stored"
                size="small"
                sx={{
                  bgcolor: '#F56423',
                  color: '#FFF',
                  border: '1px solid #F56423',
                  fontWeight: 600,
                  fontSize: 11,
                  height: 22,
                }}
              />
            )}
            {isEditedByDoctor && !isConfirmed && (
              <Chip
                label="Edited by Doctor"
                size="small"
                sx={{
                  bgcolor: '#EFF6FF',
                  color: '#1D4ED8',
                  border: '1px solid #BFDBFE',
                  fontWeight: 600,
                  fontSize: 11,
                  height: 22,
                }}
              />
            )}
            {draftSavedAt && (
              <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: 11, ml: 0.5 }}>
                Draft saved: {new Date(draftSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            )}
          </Box>
        }
      />

      <Divider sx={{ borderColor: '#F1F5F9' }} />

      <CardContent sx={{ pt: 2, pb: 1 }}>
        {children}
      </CardContent>

      <Divider sx={{ borderColor: '#F1F5F9' }} />

      <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5, bgcolor: '#FAFAFA' }}>
        <Typography variant="caption" sx={{ color: '#94A3B8' }}>
          
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {customAction}

          {!isConfirmed ? (
            <Tooltip
              title={
                !canConfirm
                  ? `Requires permission: ${requiredPermission}`
                  : 'Write this card to patient records'
              }
            >
              <span>
                <Button
                  variant="contained"
                  size="small"
                  onClick={onConfirm}
                  disabled={!canConfirm || isConfirming}
                  data-testid={`confirm-btn-${cardType}`}
                  startIcon={
                    isConfirming ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : !canConfirm ? (
                      <LockIcon fontSize="small" />
                    ) : (
                      <CheckCircleIcon fontSize="small" />
                    )
                  }
                  sx={{
                    bgcolor: '#F56423',
                    color: '#FFF',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 2,
                    '&.Mui-disabled': {
                      bgcolor: '#E2E8F0',
                      color: '#94A3B8',
                    },
                  }}
                >
                  {isConfirming ? 'Saving...' : 'Confirm & Save'}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              variant="outlined"
              size="small"
              disabled
              startIcon={<CheckCircleIcon />}
              sx={{
                borderColor: '#10B981',
                color: '#10B981',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
              }}
            >
              Saved
            </Button>
          )}
        </Box>
      </CardActions>
    </Card>
  );
};

export default AiCardBase;
